/**
 * useDviFindingGroups - Hook for managing Phase 3/4 Finding Groups
 * 
 * Provides:
 * - Fetching finding groups for a session
 * - Auto-grouping media via edge function
 * - Analyzing individual groups
 * - Phase 4: Generating communication notes
 * - Customer decision filtering
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

interface RecommendedJob {
  catalog_job_id: string;
  minutes: number;
  notes?: string;
}

interface RecommendedPart {
  name: string;
  quantity: number;
}

export interface FindingGroup {
  id: string;
  shop_id: string;
  ticket_id: string;
  session_id: string;
  title: string | null;
  summary: string | null;
  condition: 'green' | 'yellow' | 'red' | null;
  confidence: number | null;
  procedure_outline: string | null;
  recommended_jobs: RecommendedJob[];
  recommended_parts: RecommendedPart[];
  shop_supplies: string[];
  tools_required: string[];
  // Phase 4: Communication Intelligence
  advisor_summary: string | null;
  technician_notes: string | null;
  status: 'pending' | 'analyzed' | 'reviewed' | 'approved' | 'rejected';
  customer_item_token: string;
  customer_decision: 'none' | 'approved' | 'declined';
  customer_decided_at: string | null;
  customer_notes: string | null;
  added_to_ticket_at: string | null;
  added_to_ticket_by: string | null;
  created_at: string;
  media_count?: number;
  cover_thumbnail_url?: string | null;
}

interface FindingGroupWithMedia extends FindingGroup {
  media: Array<{
    id: string;
    storage_path: string;
    media_type: 'image' | 'video';
  }>;
}

export function useDviFindingGroups(sessionId: string | null, ticketId: string) {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Fetch finding groups for this session
  const { data: findingGroups = [], isLoading, refetch } = useQuery({
    queryKey: ['dvi-finding-groups', sessionId],
    queryFn: async () => {
      if (!sessionId) return [];
      
      // Fetch groups (Phase 4: include advisor_summary, technician_notes)
      const { data: groups, error } = await supabase
        .from('ticket_dvi_finding_groups')
        .select('*')
        .eq('session_id', sessionId)
        .order('created_at', { ascending: true });

      if (error) throw error;

      // Fetch media counts per group
      const { data: mediaCounts } = await supabase
        .from('ticket_dvi_media')
        .select('finding_group_id')
        .eq('session_id', sessionId)
        .not('finding_group_id', 'is', null);

      const countMap = new Map<string, number>();
      (mediaCounts || []).forEach((m: any) => {
        countMap.set(m.finding_group_id, (countMap.get(m.finding_group_id) || 0) + 1);
      });

      return (groups || []).map(g => ({
        ...g,
        recommended_jobs: (g.recommended_jobs || []) as unknown as RecommendedJob[],
        recommended_parts: (g.recommended_parts || []) as unknown as RecommendedPart[],
        shop_supplies: (g.shop_supplies || []) as unknown as string[],
        tools_required: (g.tools_required || []) as unknown as string[],
        media_count: countMap.get(g.id) || 0,
      })) as FindingGroup[];
    },
    enabled: !!sessionId,
  });

  // Auto-group media mutation
  const autoGroupMutation = useMutation({
    mutationFn: async () => {
      if (!sessionId) throw new Error('No session');
      const { data, error } = await supabase.functions.invoke('dvi-suggest-finding-groups', {
        body: { session_id: sessionId },
      });
      if (error) throw error;
      return data as { success: boolean; groups_created: number; media_assigned: number };
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['dvi-finding-groups', sessionId] });
      queryClient.invalidateQueries({ queryKey: ['dvi-media', ticketId] });
      toast({
        title: 'Media grouped',
        description: `Created ${data.groups_created} finding group(s) from ${data.media_assigned} media`,
      });
    },
    onError: (error: any) => {
      toast({ title: 'Grouping failed', description: error.message, variant: 'destructive' });
    },
  });

  // Analyze group mutation
  const analyzeGroupMutation = useMutation({
    mutationFn: async (findingGroupId: string) => {
      const { data, error } = await supabase.functions.invoke('dvi-analyze-finding-group', {
        body: { finding_group_id: findingGroupId },
      });
      if (error) throw error;
      return data as { success: boolean; condition: string; title: string; recommended_jobs_count: number };
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['dvi-finding-groups', sessionId] });
      toast({
        title: 'Analysis complete',
        description: `${data.title} - ${data.recommended_jobs_count} job(s) recommended`,
      });
    },
    onError: (error: any) => {
      toast({ title: 'Analysis failed', description: error.message, variant: 'destructive' });
    },
  });

  // Phase 4: Generate communication notes mutation
  const generateNotesMutation = useMutation({
    mutationFn: async (findingGroupId: string) => {
      const { data, error } = await supabase.functions.invoke('dvi-generate-communication-notes', {
        body: { finding_group_id: findingGroupId },
      });
      if (error) throw error;
      return data as { success: boolean; advisor_summary: string; technician_notes: string };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['dvi-finding-groups', sessionId] });
      toast({
        title: 'Notes generated',
        description: 'Advisor summary and technician notes are ready',
      });
    },
    onError: (error: any) => {
      toast({ title: 'Generation failed', description: error.message, variant: 'destructive' });
    },
  });

  // Update group title/summary
  const updateGroupMutation = useMutation({
    mutationFn: async ({ groupId, updates }: { groupId: string; updates: { title?: string; summary?: string; advisor_notes?: string } }) => {
      const { error } = await supabase
        .from('ticket_dvi_finding_groups')
        .update(updates)
        .eq('id', groupId);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['dvi-finding-groups', sessionId] });
    },
  });

  // Computed: filter by condition and decision
  const analyzedGroups = findingGroups.filter(g => g.status !== 'pending');
  const pendingGroups = findingGroups.filter(g => g.status === 'pending');
  const redGroups = findingGroups.filter(g => g.condition === 'red');
  const yellowGroups = findingGroups.filter(g => g.condition === 'yellow');
  const greenGroups = findingGroups.filter(g => g.condition === 'green');
  
  const approvedByCustomer = findingGroups.filter(g => 
    g.customer_decision === 'approved' && !g.added_to_ticket_at
  );
  const declinedByCustomer = findingGroups.filter(g => g.customer_decision === 'declined');

  // Phase 4: Groups with/without notes
  const groupsWithNotes = findingGroups.filter(g => g.advisor_summary || g.technician_notes);
  const groupsNeedingNotes = analyzedGroups.filter(g => !g.advisor_summary && !g.technician_notes);

  return {
    findingGroups,
    isLoading,
    refetch,
    
    // Filtered lists
    analyzedGroups,
    pendingGroups,
    redGroups,
    yellowGroups,
    greenGroups,
    approvedByCustomer,
    declinedByCustomer,
    
    // Phase 4: Notes status
    groupsWithNotes,
    groupsNeedingNotes,
    
    // Mutations
    autoGroup: () => autoGroupMutation.mutateAsync(),
    isAutoGrouping: autoGroupMutation.isPending,
    
    analyzeGroup: (groupId: string) => analyzeGroupMutation.mutateAsync(groupId),
    isAnalyzing: analyzeGroupMutation.isPending,
    analyzingGroupId: analyzeGroupMutation.variables,
    
    // Phase 4: Generate notes
    generateNotes: (groupId: string) => generateNotesMutation.mutateAsync(groupId),
    isGeneratingNotes: generateNotesMutation.isPending,
    generatingNotesGroupId: generateNotesMutation.variables,
    
    updateGroup: updateGroupMutation.mutate,
  };
}
